import React, { useContext } from 'react'
import { FormLayoutRow, Well } from '@pushly/aqe/lib/components'
import { Popover, Switch } from 'antd'
import { ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { useService } from '@pushly/aqe/lib/hooks/use-service'
import { AppService } from '../../../../services'
import {
    isWebPromptGroupDisplayConditions,
    WebPromptGroupDisplayConditions,
} from '@pushly/models/lib/structs/prompts/prompt-group-display-conditions'
import { PromptGroupDisplayCondition } from '@pushly/models/lib/enums/prompts/prompt-group-display-condition'
import { PromptGroupConditions } from '@pushly/models/lib/structs/prompts/prompt-group-conditions'
import { PromptEditorDispatchContext } from '../reducer'
import { PromptEditorContext } from '../context'
import classNames from 'classnames'
import { DEFAULT_DESKTOP_DISPLAY_CONDITION, DEFAULT_MOBILE_DISPLAY_CONDITION } from '../constants'

type Props = {
    onSubmit: () => void
    onCancel: () => void
}

export const PromptGroupOptionsEditor: React.FC<Props> = ({ onSubmit, onCancel }) => {
    const dispatch = useContext(PromptEditorDispatchContext)
    const context = useContext(PromptEditorContext)
    const { loading, promptGroup, isAutoShowEnabled } = context

    const appSvc = useService(AppService)

    const groupConditions = promptGroup?.conditions ?? new PromptGroupConditions()
    const groupDisplayConditions = isWebPromptGroupDisplayConditions(groupConditions.display)
        ? groupConditions.display
        : new WebPromptGroupDisplayConditions()

    const desktopDisplayCondition = groupDisplayConditions.desktop ?? DEFAULT_DESKTOP_DISPLAY_CONDITION
    const isDesktopEnabled = desktopDisplayCondition === PromptGroupDisplayCondition.ENABLED
    const isDesktopBypassEnabled = desktopDisplayCondition === PromptGroupDisplayCondition.PASS

    const mobileDisplayCondition = groupDisplayConditions.mobile ?? DEFAULT_MOBILE_DISPLAY_CONDITION
    const isMobileEnabled = mobileDisplayCondition === PromptGroupDisplayCondition.ENABLED
    const isMobileBypassEnabled = mobileDisplayCondition === PromptGroupDisplayCondition.PASS

    function handleGroupDisplayConditionsChange(conditions: WebPromptGroupDisplayConditions) {
        if (loading) return
        const groupUpdate = promptGroup.clone()
        groupUpdate.conditions = groupConditions.clone()
        groupUpdate.conditions.display = conditions
        dispatch({ type: 'set-prompt-group', group: groupUpdate })
    }

    return (
        <Well
            className="generic-well prompt-group-options-well"
            title="Additional Options"
            submitText="Save Prompt"
            onSubmit={onSubmit}
            onCancel={onCancel}
        >
            <FormLayoutRow
                className="auto-prompt-toggle"
                loading={loading}
                skeleton={{ width: 250 }}
                layout="vertical"
                label={null}
                value={
                    <div className="switch-row with-divider">
                        <div className="switch-left">
                            <Switch
                                size="small"
                                defaultChecked={isAutoShowEnabled}
                                onChange={(enabled) => {
                                    dispatch({ type: 'set-is-auto-show-enabled', enabled })
                                }}
                            />
                        </div>
                        <div className="switch-right">
                            <span>
                                Automatically prompt users
                                <Popover
                                    getPopupContainer={appSvc.getAppContainer}
                                    overlayClassName="pe-popover"
                                    content={
                                        <div>
                                            By default this prompt will automatically show to your users if all
                                            conditions are met. You may choose to disable this behavior and trigger the
                                            prompt manually after the user has performed a specific action on your
                                            website via the Platform SDK. Contact your account manager for technical
                                            implementation information.
                                        </div>
                                    }
                                    trigger="hover"
                                >
                                    <ExclamationCircleOutlined className="info-icon" />
                                </Popover>
                            </span>
                        </div>
                    </div>
                }
            />

            <FormLayoutRow
                className="desktop-position-toggle"
                loading={loading}
                skeleton={{ width: 250 }}
                layout="vertical"
                label={null}
                value={
                    <div
                        className={classNames('switch-row', {
                            ['with-divider']: isDesktopEnabled,
                        })}
                    >
                        <div className="switch-left">
                            <Switch
                                size="small"
                                defaultChecked={isDesktopEnabled}
                                onChange={(enabled) => {
                                    const conditionsUpdate = groupDisplayConditions.clone()
                                    conditionsUpdate.desktop = enabled
                                        ? PromptGroupDisplayCondition.ENABLED
                                        : PromptGroupDisplayCondition.PASS
                                    handleGroupDisplayConditionsChange(conditionsUpdate)
                                }}
                            />
                        </div>
                        <div className="switch-right">Show prompt to desktop users</div>
                    </div>
                }
            />

            {!isDesktopEnabled && (
                <FormLayoutRow
                    className="desktop-position-pass-toggle"
                    layout="vertical"
                    label={null}
                    value={
                        <div className="switch-row child-row with-divider">
                            <div className="switch-left">
                                <Switch
                                    size="small"
                                    defaultChecked={isDesktopBypassEnabled}
                                    onChange={(enabled) => {
                                        const conditionsUpdate = groupDisplayConditions.clone()
                                        conditionsUpdate.desktop = enabled
                                            ? PromptGroupDisplayCondition.PASS
                                            : PromptGroupDisplayCondition.DISABLED
                                        handleGroupDisplayConditionsChange(conditionsUpdate)
                                    }}
                                />
                            </div>
                            <div className="switch-right">Continue to next priority prompt</div>
                        </div>
                    }
                />
            )}

            <FormLayoutRow
                className="mobile-position-toggle"
                loading={loading}
                skeleton={{ width: 250 }}
                layout="vertical"
                label={null}
                value={
                    <div className="switch-row">
                        <div className="switch-left">
                            <Switch
                                size="small"
                                defaultChecked={isMobileEnabled}
                                onChange={(enabled) => {
                                    const conditionsUpdate = groupDisplayConditions.clone()
                                    conditionsUpdate.mobile = enabled
                                        ? PromptGroupDisplayCondition.ENABLED
                                        : PromptGroupDisplayCondition.PASS
                                    handleGroupDisplayConditionsChange(conditionsUpdate)
                                }}
                            />
                        </div>
                        <div className="switch-right">
                            Show prompt to mobile users
                            <Popover content="Mobile placement also includes tablet devices" trigger="hover">
                                <InfoCircleOutlined className="info-icon" />
                            </Popover>
                        </div>
                    </div>
                }
            />

            {!isMobileEnabled && (
                <FormLayoutRow
                    className="mobile-position-pass-toggle"
                    layout="vertical"
                    label={null}
                    value={
                        <div className="switch-row child-row">
                            <div className="switch-left">
                                <Switch
                                    size="small"
                                    defaultChecked={isMobileBypassEnabled}
                                    onChange={(enabled) => {
                                        const conditionsUpdate = groupDisplayConditions.clone()
                                        conditionsUpdate.mobile = enabled
                                            ? PromptGroupDisplayCondition.PASS
                                            : PromptGroupDisplayCondition.DISABLED
                                        handleGroupDisplayConditionsChange(conditionsUpdate)
                                    }}
                                />
                            </div>
                            <div className="switch-right">Continue to next priority prompt</div>
                        </div>
                    }
                />
            )}
        </Well>
    )
}
